/**
 * The single centered-dialog primitive for every app (BL-1247, ratifying the
 * `Modal` row of the ADR 030 Canon).
 *
 * The merge collapses two implementations of the same job — bidder-web's
 * `Modal` (the canonical base per ADR 030 § 3b: it owns panel + header + footer
 * + size in one component) and admin-web's `LazyModal` (which owned only a
 * backdrop and centring, leaving every caller to assemble its own chrome).
 *
 * WHY the component owns the chrome: admin had ~66 call sites each hand-rolling
 * a `bg-white rounded-lg shadow-xl max-w-*` card, its own `PanelHeader` and its
 * own `FormFooter`. They drifted apart badly enough that BUG-1009 had to go
 * re-unify the headers. A convention cannot hold chrome consistent across 66
 * copies; a component can. Callers now hand over a title and content.
 *
 * WHY `panel` is an explicit prop rather than inferred from "did you pass a
 * title?": the two skins want opposite defaults. A chrome-less bidder modal is
 * still a white card, so bidder always panels. Admin's un-migrated callers
 * supply their OWN card, so panelling them would nest a card inside a card —
 * they pass `panel={false}` and get byte-for-byte the old `LazyModal` shape.
 * Making it explicit keeps that difference readable instead of emergent.
 *
 * WHY the portal and scroll lock are NOT here: they are genuinely per-app
 * infrastructure, not appearance. Admin layers through `overlay-layer`'s
 * z-index context (BL-909) so a modal opened from inside a slide-out stacks
 * above it; bidder uses `ModalPortal`'s ref-counted, iOS-safe body lock. Each
 * skin supplies its own and passes the computed z-indices down through
 * `backdropStyle` / `containerStyle`.
 *
 * Close-only-on-confirmed-success: Modal is fully controlled and NEVER
 * self-closes on an internal action. It calls `onClose` only for Escape, a
 * backdrop click, or the close (X) button. Parents flip `isOpen=false` on
 * confirmed success; error content stays rendered inside the open modal
 * (`error-handling-chain.md`).
 */
'use client';

import clsx from 'clsx';
import { useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { X } from 'lucide-react';

import { useFocusTrap, type FocusTrapInitialFocus } from '../hooks/useFocusTrap';
import { useBackdropDismiss } from '../hooks/useBackdropDismiss';
import { useTopmostEscape } from '../hooks/useTopmostEscape';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot is optional: a consumer with no dialect gets the bare accessible
 * structure, which is the right default for a third app that hasn't declared
 * one yet.
 */
export interface ModalDialect {
    /** The dimmed layer behind the panel. */
    backdrop?: string;
    /** The full-viewport layer that centres (or edge-aligns) the panel. */
    container?: string;
    /** The panel card — background, radius, shadow, layout. */
    panel?: string;
    /** Size key → max-width class. Keys are per-dialect; unknown keys resolve to nothing. */
    size?: Record<string, string>;
    /** The header row holding the title and close control. */
    header?: string;
    /** The title text inside the header row. */
    title?: string;
    /** The close (X) control when it sits inside the header row. */
    closeButton?: string;
    /** The close (X) control when there is no header and it floats over the panel. */
    closeButtonFloating?: string;
    /**
     * Wrapper around `children` inside the panel — this is where a dialect puts
     * its body gutter. Omit for no wrapper. Reached only in chrome mode: a
     * `panel={false}` caller's `children` IS its own card, and the bare branch
     * renders it untouched.
     */
    body?: string;
    /** The footer row. Omit for no wrapper — the footer node renders bare. */
    footer?: string;
}

/**
 * Props for {@link Modal}.
 */
export interface ModalProps {
    /** Whether the modal is open. Closed → nothing renders. */
    isOpen: boolean;
    /** Called for Escape, backdrop click, or the X button — never on internal actions. */
    onClose: () => void;
    /** Modal content. */
    children: ReactNode;
    /**
     * Draw the panel card and its chrome. Default `true`. Pass `false` for the
     * compatibility shape where the caller supplies its own card — see the
     * file header.
     */
    panel?: boolean;
    /** Header title. A string also becomes the dialog's accessible name. */
    title?: ReactNode;
    /** Optional secondary text under the title. Ignored when `header` is supplied. */
    subtitle?: ReactNode;
    /** A pre-assembled header node, replacing the built-in title row entirely. */
    header?: ReactNode;
    /** Footer content, rendered in the footer slot. */
    footer?: ReactNode;
    /** Key into `dialect.size`. */
    size?: string;
    /** Accessible label, used when `title` is absent or non-textual. */
    ariaLabel?: string;
    /**
     * `id` of an element that names the dialog. For a skin supplying a
     * pre-assembled `header` node, this is how the dialog still points at the
     * visible heading inside it — the built-in `title` path can't, since the
     * core never renders that heading itself. Wins over `ariaLabel`; pointing
     * at the visible title beats a duplicated string.
     */
    ariaLabelledBy?: string;
    /** Render a close (X) button. Default true. Ignored when `header` is supplied. */
    showCloseButton?: boolean;
    /**
     * Render the dimmed backdrop as a child of the container. Default `true`.
     *
     * Pass `false` when the skin renders its own backdrop as a PORTAL SIBLING
     * of the container — which admin must, because its un-migrated callers
     * supply a statically-positioned panel div. CSS paints positioned
     * descendants above non-positioned ones regardless of DOM order, so a
     * nested `absolute inset-0` backdrop would cover those callers' own panels.
     * Bidder has no such problem: its panel is `relative`, so it paints above.
     */
    backdrop?: boolean;
    /** Close when the backdrop is clicked. Default true. Requires `backdrop`. */
    closeOnBackdrop?: boolean;
    /**
     * Close when the container itself is clicked (i.e. the click landed on the
     * centring layer, not on content inside it). This is the dismissal path for
     * a skin using `backdrop={false}`, where there is no backdrop child to
     * click — it is how admin's `LazyModal` has always worked.
     */
    closeOnContainerClick?: boolean;
    /** Close when Escape is pressed. Default true. */
    closeOnEscape?: boolean;
    /**
     * Where focus lands on open: the first control (default) or the dialog
     * itself. See `FocusTrapInitialFocus` — `'container'` keeps a phone sheet
     * from raising the keyboard on open (BL-1998).
     */
    initialFocus?: FocusTrapInitialFocus;
    /** Extra classes on the panel, merged after `dialect.panel`. */
    className?: string;
    /** Extra classes on the centring container, merged after `dialect.container`. */
    containerClassName?: string;
    /** Extra classes on the backdrop, merged after `dialect.backdrop`. */
    backdropClassName?: string;
    /** Inline style on the backdrop — the sanctioned home for a skin-computed z-index. */
    backdropStyle?: CSSProperties;
    /** Inline style on the container — the sanctioned home for a skin-computed z-index. */
    containerStyle?: CSSProperties;
    /** Per-app appearance binding. See {@link ModalDialect}. */
    dialect?: ModalDialect;
    /**
     * Automation handle (`data-testid`) on the `role="dialog"` root. Opt-in:
     * pages routinely stack several modals and the component has no other
     * stable id, so a shared default would tag them all identically
     * (`automation-handles.md`). No `data-state` is owed — a closed Modal
     * renders nothing, so presence IS the open state.
     */
    testId?: string;
}

/**
 * Close (X) control. Rendered by the component so no caller has to draw one —
 * the affordance BUG-1009 found drifting when each modal owned its own.
 *
 * @param props - click handler and the dialect class for this placement.
 * @returns The close button.
 */
function CloseButton({ onClose, className }: { onClose: () => void; className?: string }) {
    return (
        <button type="button" onClick={onClose} aria-label="Close" className={className}>
            <X className="h-5 w-5" aria-hidden="true" />
        </button>
    );
}

/**
 * Standard centered modal dialog. See the file header for the chrome it owns,
 * why `panel` is explicit, and the close-only-on-confirmed-success contract.
 *
 * @param props - see {@link ModalProps}
 * @returns The dialog tree, or nothing when closed. Does NOT portal — the
 * calling skin owns the portal and the body scroll lock.
 */
export default function Modal({
    isOpen,
    onClose,
    children,
    panel = true,
    title,
    subtitle,
    header,
    footer,
    size,
    ariaLabel,
    ariaLabelledBy,
    showCloseButton = true,
    backdrop = true,
    closeOnBackdrop = true,
    closeOnContainerClick = false,
    closeOnEscape = true,
    initialFocus = 'first',
    className,
    containerClassName,
    backdropClassName,
    backdropStyle,
    containerStyle,
    dialect = {},
    testId,
}: ModalProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    // BUG-2302: a drag released over the container is not a click (shared with every overlay, BUG-2308).
    const containerDismiss = useBackdropDismiss({ enabled: closeOnContainerClick, onDismiss: onClose });
    const titleId = useId();
    const hasTextTitle = typeof title === 'string';

    // Escape is owned here so every consumer gets it — three of bidder's five
    // originally-migrated modals had no Escape handling before BL-713. Routed
    // through the shared stack (BUG-1399) so a nested overlay dismisses ITSELF
    // rather than collapsing the whole stack: peer listeners on `document` all
    // fire for one Escape, so without an explicit topmost check, opening the
    // photo lightbox from the lot editor and pressing Escape closed both.
    useTopmostEscape(isOpen && closeOnEscape, onClose);

    useFocusTrap(containerRef, isOpen, initialFocus);

    if (!isOpen) return null;

    const showHeaderRow = panel && (header != null || title != null);
    // A floating X is for a panel with no header at all. When `header` is
    // supplied the header node owns its own close control (admin's PanelHeader
    // does), so injecting a second one here would render two X's.
    const showFloatingClose = panel && showCloseButton && header == null && title == null;

    // Only ever consumed inside the `panel ? …` branch below — the bare branch
    // renders `children` directly — so the body wrapper is already confined to
    // chrome mode by construction. No `panel &&` guard here: it would be a
    // condition that can never be false at its point of use.
    const body = dialect.body ? <div className={dialect.body}>{children}</div> : children;

    return (
        <div
            ref={containerRef}
            tabIndex={-1}
            className={clsx(dialect.container, containerClassName)}
            style={containerStyle}
            data-testid={testId}
            role="dialog"
            aria-modal="true"
            // A skin-supplied `ariaLabelledBy` points at the heading inside a
            // pre-assembled `header` node — the only naming path available when
            // the core isn't the one rendering the title.
            aria-label={hasTextTitle || ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={hasTextTitle ? titleId : ariaLabelledBy}
            // Only a true click on the centring layer dismisses (BUG-2302); the rule
            // lives in useBackdropDismiss, shared with every other overlay.
            {...containerDismiss}
        >
            {backdrop && (
                <div
                    data-testid="modal-backdrop"
                    className={clsx(dialect.backdrop, backdropClassName)}
                    style={backdropStyle}
                    onClick={closeOnBackdrop ? onClose : undefined}
                    aria-hidden="true"
                />
            )}

            {panel ? (
                <div className={clsx(dialect.panel, size ? dialect.size?.[size] : undefined, className)}>
                    {showHeaderRow &&
                        (header ?? (
                            <div className={dialect.header}>
                                <div className="min-w-0">
                                    <h2 id={titleId} className={dialect.title}>
                                        {title}
                                    </h2>
                                    {subtitle != null && <p className="mt-0.5 truncate text-sm opacity-70">{subtitle}</p>}
                                </div>
                                {showCloseButton && <CloseButton onClose={onClose} className={dialect.closeButton} />}
                            </div>
                        ))}

                    {showFloatingClose && <CloseButton onClose={onClose} className={dialect.closeButtonFloating} />}

                    {body}

                    {footer != null &&
                        (dialect.footer ? <div className={dialect.footer}>{footer}</div> : footer)}
                </div>
            ) : (
                children
            )}
        </div>
    );
}
